import SellingUnit from "@/components/SellingUnit";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang, useL } from "@/i18n";
import { api, money, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, coupon, applyCoupon, clearCoupon } = useCart();
  const { t, lang } = useLang();
  const L = useL();
  const [promo, setPromo] = useState(coupon?.code || "");
  const discount = coupon?.discount || 0;

  const applyPromo = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: promo, subtotal });
      applyCoupon(data.code, data.discount);
      toast.success(`${lang === "fr" ? "Code appliqué" : "Coupon applied"}: -${money(data.discount)}`);
    } catch (e) { clearCoupon(); toast.error(apiError(e.response?.data?.detail, lang)); }
  };

  const total = Math.max(0, subtotal - discount);

  if (items.length === 0)
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-5 px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={56} className="text-navy/20" />
        <h1 className="font-display text-3xl font-bold text-navy">{t("cart.empty")}</h1>
        <Link to="/shop" className="btn-gold rounded-full px-8 py-3 text-sm font-bold">{t("common.shopNow")}</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-10" data-testid="cart-page">
      <h1 className="mb-8 font-display text-4xl font-bold text-navy">{t("cart.title")}</h1>
      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 rounded-2xl border border-border bg-cream-card p-4 card-shadow" data-testid={`cart-row-${it.slug}`}>
              <img src={it.image} alt={L(it, "name")} className="h-24 w-24 shrink-0 rounded-xl object-cover" />
              <div className="flex min-w-0 flex-1 flex-col">
                <Link to={`/product/${it.slug}`} className="font-head font-semibold text-navy hover:text-sunset">{L(it, "name")}</Link>
                <SellingUnit product={it} />
                {it.selling_unit !== "marmite" && it.weight && <span className="text-xs text-muted-foreground">{it.weight}</span>}
                <span className="mt-1 text-sm font-bold text-navy">{money(it.price)}</span>
                <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 rounded-full border border-border">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="grid h-8 w-8 place-items-center" aria-label="decrease"><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="grid h-8 w-8 place-items-center" aria-label="increase"><Plus size={14} /></button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-head font-bold text-navy">{money(it.price * it.quantity)}</span>
                    <button onClick={() => removeItem(it.product_id)} className="text-muted-foreground hover:text-sunset" aria-label="remove"><Trash2 size={18} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="h-fit rounded-2xl border border-border bg-cream-card p-6 card-shadow">
          <h3 className="mb-4 font-display text-xl font-bold text-navy">{t("common.total")}</h3>
          <div className="mb-4 flex gap-2">
            <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={t("cart.promo")} className="w-full rounded-full border border-border bg-cream px-4 py-2.5 text-sm outline-none focus:border-gold" data-testid="promo-input" />
            <button onClick={applyPromo} className="rounded-full bg-navy px-4 py-2.5 text-sm font-semibold text-cream" data-testid="promo-apply">{t("cart.apply")}</button>
          </div>
          {coupon && <div className="mb-4 flex items-center justify-between gap-3 text-sm text-palm-500">
            <span>{coupon.code}</span>
            <button type="button" onClick={() => { clearCoupon(); setPromo(""); }} className="underline" data-testid="remove-coupon">{t("common.remove")}</button>
          </div>}
          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("common.subtotal")}</span><span className="font-semibold text-navy">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("common.shipping")}</span><span className="font-semibold text-navy">{t("shipping.atCheckout")}</span></div>
            {discount > 0 && <div className="flex justify-between text-palm-500"><span>{t("common.discount")}</span><span className="font-semibold">-{money(discount)}</span></div>}
          </div>
          <div className="mt-4 flex justify-between border-t border-border pt-4">
            <span className="font-head font-bold text-navy">{t("shipping.beforeShipping")}</span>
            <span className="font-head text-xl font-bold text-navy" data-testid="cart-total">{money(total)}</span>
          </div>
          <Link to="/checkout" className="btn-gold mt-5 flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold" data-testid="checkout-btn">
            {t("cart.checkout")} <ArrowRight size={16} />
          </Link>
          <p className="mt-3 text-center text-xs text-muted-foreground">{t("checkout.usOnly")}</p>
        </div>
      </div>
    </div>
  );
}
